/** Copyright (C) 2022 Trend Micro Inc. All rights reserved. */

import {
  createSlice,
  createSelector,
  createAsyncThunk,
  PayloadAction,
} from '@reduxjs/toolkit'
import { c1UserTimeZoneSelector } from '@redux/slices/common'
import { SCAN_EVENT_KEYS } from '@constants/event'
import { getProviderName } from '@utils/providerName'
import { getFSSTimeDisplayFormat } from '@utils/time'
import eventApi from '@redux/apis/event'

const REDUCER_NAME = 'event'

const initEventState: Redux.EventState = {
  data: SCAN_EVENT_KEYS.reduce((acc, cur) => {
    Object.defineProperty(acc, cur, {
      value: {
        events: [],
        next: null,
      },
      writable: true,
      enumerable: true,
    })

    return acc
  }, {}) as Record<Redux.EventType, Response.Event.EventAPI>,
  from: null,
  to: null,
  enableEventTableType: 'malicious',
  isLoading: false,
  isInitialized: false,
}

function setEventIsLoadingAction(state: typeof initEventState) {
  state.isLoading = true
  state.isInitialized = true
}

function setEventIsNotLoadingAction(state: typeof initEventState) {
  state.isLoading = false
}

function resetEventListAction(state: typeof initEventState) {
  state.data.error = {
    events: [],
    next: null,
  }
  state.data.malicious = {
    events: [],
    next: null,
  }
}

function resetEventAction() {
  return initEventState
}

function setEventTableTypeAction(
  state: typeof initEventState,
  action: PayloadAction<Redux.EventType>
) {
  state.enableEventTableType = action.payload
}

export const fetchEvents = createAsyncThunk<
  Response.Event.EventAPI,
  Request.EventParams
>(
  `${REDUCER_NAME}/fetchEvents`,
  async ({ from, to, type, next }, { dispatch }) => {
    const data = await dispatch(
      eventApi.endpoints.getEvents.initiate({
        from,
        to,
        cursor: next,
        nextEnableEventTableType: type,
      })
    ).unwrap()

    return data
  }
)

const eventSlice = createSlice({
  name: REDUCER_NAME,
  initialState: initEventState,
  reducers: {
    setEventTableType: setEventTableTypeAction,
    resetEventList: resetEventListAction,
    resetEvent: resetEventAction,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchEvents.pending, setEventIsLoadingAction)
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.isLoading = false
        state.isInitialized = true
        state.data[state.enableEventTableType] = {
          events: [
            ...state.data[state.enableEventTableType].events,
            ...action.payload.events,
          ],
          next: action.payload.next,
        }
      })
      .addCase(fetchEvents.rejected, setEventIsNotLoadingAction)
  },
})

export const selectSelf = (state: Redux.RootState) => state.event

export const enableEventTableTypeSelector = createSelector(
  selectSelf,
  event => event.enableEventTableType
)

export const maliciousEventDataSelector = createSelector(
  selectSelf,
  event => event.data.malicious
)

export const errorEventDataSelector = createSelector(
  selectSelf,
  event => event.data.error
)

export const maliciousEventTableDataSelector = createSelector(
  maliciousEventDataSelector,
  c1UserTimeZoneSelector,
  ({ events, next }, { getC1UserTzDateInstance, c1UserTimeZoneOffsetByHr }) => {
    const maliciousEvents = events.reduce((acc, cur) => {
      const malwareTypes: string[] = []
      const malwareNames: string[] = []
      const malwares: string[] = cur?.data?.detections.map(d => {
        malwareTypes.push(d?.malwareType)
        malwareNames.push(d?.malware)

        return d?.malware
      })
      const children: Record<string, unknown>[] =
        malwareTypes.length > 1
          ? malwareTypes.map((type, idx) => ({
              filename: '',
              type,
              malware: [malwareNames[idx]],
              storage: '',
              provider: '',
              providerAccount: '',
              time: '',
            }))
          : []

      if (cur?.data?.detections?.length > 0) {
        acc.push({
          filename: cur?.data?.filename,
          type: malwareTypes,
          malware: malwares,
          storage: cur?.data?.storage,
          provider: getProviderName(cur?.data?.provider),
          providerAccount: cur?.data?.providerAccount,
          time: getFSSTimeDisplayFormat(
            getC1UserTzDateInstance(cur?.time),
            c1UserTimeZoneOffsetByHr
          ),
          ...(children.length > 0 && { children }),
        })
      }

      return acc
    }, [])

    return {
      events: maliciousEvents,
      next,
    }
  }
)

export const errorEventTableDataSelector = createSelector(
  errorEventDataSelector,
  c1UserTimeZoneSelector,
  ({ events, next }, { getC1UserTzDateInstance, c1UserTimeZoneOffsetByHr }) => {
    const errorEvents = events.reduce((acc, cur) => {
      const errorTypes: string = cur?.data?.errors
        .map(d => d?.details?.errorMessage || d?.type)
        .join(', ')

      if (cur?.data?.errors?.length > 0) {
        acc.push({
          filename: cur?.data?.filename,
          errorTypes,
          storage: cur?.data?.storage,
          provider: getProviderName(cur?.data?.provider),
          providerAccount: cur?.data?.providerAccount,
          time: getFSSTimeDisplayFormat(
            getC1UserTzDateInstance(cur?.time),
            c1UserTimeZoneOffsetByHr
          ),
        })
      }

      return acc
    }, [])

    return {
      events: errorEvents,
      next,
    }
  }
)

export const eventTableDataSelector = createSelector(
  maliciousEventTableDataSelector,
  errorEventTableDataSelector,
  enableEventTableTypeSelector,
  (maliciousEvents, errorEvents, enableEventTableType) =>
    enableEventTableType === 'malicious' ? maliciousEvents : errorEvents
)

export const eventIsLoadingSelector = createSelector(
  selectSelf,
  event => event.isLoading
)

export const eventIsInitializedSelector = createSelector(
  selectSelf,
  event => event.isInitialized
)

export const { setEventTableType, resetEvent, resetEventList } =
  eventSlice.actions

export default eventSlice.reducer
